import React, {forwardRef, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import TypewriterComponent from "typewriter-effect";
import ReactPlayer from "react-player";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faPlay, faPause, faGlobe, faHeartCirclePlus, faBagShopping, faUserLock} from '@fortawesome/free-solid-svg-icons';
import {ROLES} from "../utils/constants";
import API from "../api";
import {Button, ButtonGroup} from "../buttons";
import {useRecaptcha} from "../recaptcha";
import GooglePayProvisioningModal from "./google-pay-provisioning";
import AppleWalletProvisioningModal from "./apple-wallet-provisioning";


const LoadingSpinner = () => {
  return <div className="loader spinner"></div>
}


const CardArtwork = forwardRef((props, ref) => {
  return <div className={`cardArtwork isActive-${props.isActive}`} ref={ref}>
    <LoadingSpinner />
  </div>
});


const WALLET_LANG_MAP = {
    google: {
        'en': 'enUS',
        'es': 'esES',
        'fr': 'frFR',
        'de': 'de',
        'it': 'it',
        'pt': 'pt',
        'ja': 'jp',
        'zh': 'zhHK',
        'ru': 'ru',
        'ar': 'ar',
    },
    apple: {
        'en': 'US_UK',
        'es': 'ES',
        'fr': 'FR',
        'de': 'DE',
        'it': 'IT',
        'pt': 'PT',
        'ja': 'JP',
        'zh': 'CN',
        'ru': 'RU',
        'ar': 'AR',
    },
};

const getWalletButtonSvg = (provider, locale) => {
    const providerMap = WALLET_LANG_MAP[provider];
    if (!providerMap) return null;

    const langCode = (locale || 'en').split('-')[0].toLowerCase();
    const mapped = providerMap[langCode] ?? providerMap.en;

    return `/wallet-buttons/${provider}/${mapped}`;
};

const WalletButton = ({ provider, locale, label, disabled = false, title, onClick }) => {
    const icon = getWalletButtonSvg(provider, locale);
    if (!icon) return null;
    return (
        <button
            className={`walletButton ${disabled ? 'disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
            title={title}
        >
            <img src={`${icon}-button.svg`} alt={label} />
        </button>
    );
};

const WalletAddedIndicator = ({ label, provider = 'google' }) => (
    <div className="walletAddedIndicator" role="status">
        {provider !== 'apple' && <img src="/assets/icons/gwallet-icon.svg" alt="" />}
        <span>{label}</span>
    </div>
);

const CardFunctions = forwardRef(({ className = '', token = '', card = null, onResetPinSentSuccess, onError }, ref) => {
    const { t, i18n } = useTranslation();
    const api = API();
    const { getToken: getRecaptchaToken } = useRecaptcha();
    const [cardConfig, setCardConfig] = useState({});
    const [showGooglePayModal, setShowGooglePayModal] = useState(false);
    const [addedToGoogleWallet, setAddedToGoogleWallet] = useState(false);
    const [googleWalletError, setGoogleWalletError] = useState('');
    const [showAppleWalletModal, setShowAppleWalletModal] = useState(false);
    const [addedToAppleWallet, setAddedToAppleWallet] = useState(false);
    const [appleWalletError, setAppleWalletError] = useState('');

    const loadConfig = async () => {
        const clientConfig = await api.getClientConfig();
        setCardConfig(clientConfig.cardConfig || {});
    }

    // After the shared provisioning subdomain redirects back here, pick up
    // the result and strip it from the URL so a refresh doesn't re-show it.
    // walletProvider says which wallet (google|apple) the result belongs to —
    // without it, applying the result to both wallets would show the same
    // Success/Error state twice when a brand has both buttons enabled.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const walletResult = params.get('walletResult');
        const walletError = params.get('walletError');
        const walletProvider = params.get('walletProvider');
        if (!walletResult) return;
        if (walletResult === 'success') {
            if (walletProvider === 'google') setAddedToGoogleWallet(true);
            if (walletProvider === 'apple') setAddedToAppleWallet(true);
        }
        if (walletResult === 'failure' && walletError) {
            if (walletProvider === 'google') setGoogleWalletError(walletError);
            if (walletProvider === 'apple') setAppleWalletError(walletError);
        }
        params.delete('walletResult');
        params.delete('walletError');
        params.delete('walletProvider');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState(null, '', newUrl);
    }, []);

    const handleOpenGooglePayModal = () => {
        setGoogleWalletError('');
        setShowGooglePayModal(true);
    };

    const handleOpenAppleWalletModal = () => {
        setAppleWalletError('');
        setShowAppleWalletModal(true);
    };

    const handleClickResetPin = async () => {
        try {
            const recaptchaToken = await getRecaptchaToken('pin_reset');
            const response = await api.startResetPin(token, recaptchaToken);
            if (response && !response.error) {
                onResetPinSentSuccess();
                return;
            } else {
                const errorMsg = response?.error || response?.message || t('giftcard.resetPinError');
                onError({}, errorMsg);
            }
        } catch (error) {
            console.error('Reset PIN error:', error);
            alert(t('giftcard.resetPinError'));
        }
    };

    useEffect( () => {
        loadConfig();
    }, [] );

    return <div className={`cardFunctions ${className}`} ref={ref}>
        <ButtonGroup>
            {cardConfig.hasSetPIN ? <Button className="resetPin" label={t('giftcard.resetPin')} onClick={handleClickResetPin} /> : null}
            <div className="walletButtonsGroup">
                {cardConfig.hasAppleWallet ? (
                    addedToAppleWallet ? (
                        <div className="walletAddedWrapper">
                            <span className="walletAddedWrapper__success">
                                {t('appleWallet.successLabel', 'Success!')}
                            </span>
                            <WalletAddedIndicator provider="apple" label={t('appleWallet.added', 'Added to Apple Wallet')} />
                            {/* Keep the button clickable after Success so the user can
                                start the Apple Wallet flow again for another card. */}
                            <WalletButton
                                provider="apple"
                                locale={i18n.language}
                                label={t('appleWallet.buttonLabel', 'Add to Apple Wallet')}
                                onClick={handleOpenAppleWalletModal}
                            />
                        </div>
                    ) : (
                        <div className="walletErrorWrapper">
                            {appleWalletError && (
                                <span className="walletErrorWrapper__message">{appleWalletError}</span>
                            )}
                            <WalletButton
                                provider="apple"
                                locale={i18n.language}
                                label={t('appleWallet.buttonLabel', 'Add to Apple Wallet')}
                                onClick={handleOpenAppleWalletModal}
                            />
                        </div>
                    )
                ) : null}
                {cardConfig.hasGooglePay ? (
                    addedToGoogleWallet ? (
                        <div className="walletAddedWrapper">
                            <span className="walletAddedWrapper__success">
                                {t('googlePay.successLabel', 'Success!')}
                            </span>
                            <WalletAddedIndicator label={t('googlePay.added', 'Added to Google Wallet')} />
                            {/* Keep the button clickable after Success so the user can
                                start the Google Wallet flow again for another card. */}
                            <WalletButton
                                provider="google"
                                locale={i18n.language}
                                label={t('googlePay.buttonLabel', 'Add to Google Pay')}
                                onClick={handleOpenGooglePayModal}
                            />
                        </div>
                    ) : (
                        <div className="walletErrorWrapper">
                            {googleWalletError && (
                                <span className="walletErrorWrapper__message">{googleWalletError}</span>
                            )}
                            <WalletButton
                                provider="google"
                                locale={i18n.language}
                                label={t('googlePay.buttonLabel', 'Add to Google Pay')}
                                onClick={handleOpenGooglePayModal}
                            />
                        </div>
                    )
                ) : null}
            </div>
        </ButtonGroup>
        {showGooglePayModal && (
            <GooglePayProvisioningModal
                token={token}
                cardConfig={cardConfig}
                onClose={() => setShowGooglePayModal(false)}
            />
        )}
        {showAppleWalletModal && (
            <AppleWalletProvisioningModal
                token={token}
                onClose={() => setShowAppleWalletModal(false)}
            />
        )}
    </div>
});


const BrandHeader = forwardRef((props, ref) => {
  return <div className={"brandHeader"} ref={ref}>
    <div className="logo"></div>
  </div>
});


const InfoPanel = forwardRef(({ className = '', text='' }, ref) => {
  return <div className={`infoPanel ${className} flexRow`} ref={ref}>
      <TypewriterComponent options={{ strings: [text], loop: true, autoStart: true, delay: 20, pauseFor: 90000 }} />
  </div>
});






const formatTrxDate = (dateStr) => {
  if (!dateStr) return "";
  // API usually returns YYYY-MM-DD; fall back to original if parsing fails
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTrxTime = (timeStr) => {
  if (!timeStr) return "";
  // API usually returns HH:MM:SS (24h) — show 12-hour with AM/PM
  const match = String(timeStr).match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return timeStr;
  let hours = Number.parseInt(match[1], 10);
  const minutes = match[2];
  if (Number.isNaN(hours) || hours > 23) return timeStr;
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${period}`;
};

const trx2TableRow = (trx, i) => {
  const sign = trx.creditOrDebit === 'C' ? '+' : '-';
  const amount = Number.parseFloat(trx.transactionAmount).toFixed(2)
  const merchantName = trx.merchantName ? trx.merchantName.replaceAll('\\', ' ') : ""
  const description = trx.description ? String(trx.description).trim() : ""
  const status = trx.status ? trx.status.replaceAll(/\W+/g,'').slice(0, 10) : '';
  return <div className={`trx ${status}`} key={i}>
    <span className={"trxDate"}>{formatTrxDate(trx.transactionDate)}</span>
    <span className={"trxTime"}>{formatTrxTime(trx.transactionTime)}</span>
    {/* <span className={"trxId"}>{trx.transactionId}</span> */}
    <span className={"trxAmount"}>{sign} {trx.currencyCode} {amount}</span>
    <span className={"trxMerchant"} title={merchantName || undefined}>{merchantName}</span>
    <span
      className={"trxDescription"}
      title={description || undefined}
      aria-label={description || undefined}
    >
      {description}
    </span>
  </div>
}

const Statement = forwardRef(({ className, transactions }, ref) => {
  const { t, i18next } = useTranslation();
  const hasTransactions = (transactions && transactions.length > 0);
  return <div className={`transactionList ${className}`} ref={ref}>
    <h2>{t('giftcard.transactionList')}</h2>
    {hasTransactions ? transactions.map(trx2TableRow) : t('giftcard.noTransactionsYet')}
  </div>

});


const LanguageSwitch = ({ i18next, langs = [] }) => {
  //const {t, i18next} = useTranslation();
  ;
  const changeLanguageHandler = async (e) => {
    const lang = e.currentTarget.dataset.lang;
    await i18next.changeLanguage(lang);
    window.location.reload();
  }

  return <div className="languageSwitch">
    <FontAwesomeIcon icon={faGlobe} />
    {langs.map((lang, i) => <span data-lang={lang} key={i} onClick={changeLanguageHandler}>{lang}</span>)}
  </div>
}



const InputTokenForm = forwardRef(({ className = '' }, ref) => {
  const { t, i18next } = useTranslation();
  const [errorMsg, setErrorMsg] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(false);

  const cleanTokenInput = (input) => {
    return input.replaceAll(/\D+/g, '');
  }

  const isValid = (token) => {
    return token && token.length === 9 && !!token.match(/^\d{9}$/);
  }

  const handleKeyUp = (e) => {
    const token = cleanTokenInput(e.target.value);
    setErrorMsg('');
    setIsTokenValid( isValid( token ) );
    if (e.key === "Enter") handleTokenChange(e);
  }

  const handleTokenChange = (e) => {
    const token = cleanTokenInput(e.target.value);
    if (token.length === 9) window.location = `/${token}`;
    else setErrorMsg(t('giftcard.tokenHasToBe9Digits'));
  }

  return <div className={`inputTokenForm ${className}`} ref={ref}>
    <label>{t('giftcard.enterToken')}</label>
    <input inputMode="numeric" maxLength="9" min={1} max={999999999} placeholder="000000000" name="token"
      onBlur={handleTokenChange}
      onKeyUp={handleKeyUp}
    />
    <button onClick={handleTokenChange} className={`buttonGo disabled-${!isTokenValid}`} data-testid="submit button">
        {t("giftcard.goCheck")}
    </button>
    <span className={`error ${errorMsg ? '' : 'hidden'}`}>{errorMsg}</span>
  </div>
});

const GifterOrGifteeModule =  forwardRef(
    ({ className = 'hidden', isActive = false, onSelection }, ref) => {
  const { t, i18next } = useTranslation();
  const handleOnClick = (e) => {
    onSelection(e.target.value);
  }
  return <div className={`gifterOrGifteeModule ${className} isActive-${isActive}`} ref={ref}>
    <h2>
      {t("giftcard.cardInactiveWhoIsItFor")}</h2>
    <button className="button half" onClick={handleOnClick} value={ROLES.GIFTER}>
      <FontAwesomeIcon icon={faHeartCirclePlus}/> {t("giftcard.itIsForSomeoneElse")}
    </button>
    <button className="button half" onClick={handleOnClick} value={ROLES.GIFTEE}>
      <FontAwesomeIcon icon={faBagShopping}/> {t("giftcard.itIsForMe")}
    </button>
  </div>
    })

const MicrositeFooter = forwardRef(({className = '', links = [], baseUrl = ''}, ref) => {
  const {t, i18next} = useTranslation();
  links.forEach(link => {
    link.url = link.url.replace("{base_url}", baseUrl);
    link.id = link.id || link.text.replace(".", "-");
  })
  console.log("Footer links:", links);

  return <div className={`micrositeFooter ${className}`} ref={ref}>
    <div className="links">
      {links.map((link, i) => <a key={i} id={link.id} href={`${link.url}`} target={link.target} rel={link.target ? "noopener noreferrer" : undefined}>
        <div className="icon"></div>
        <span>{t(link.text)}</span>
      </a>)}
    </div>
  </div>
});



export {
  InfoPanel, BrandHeader, CardArtwork,
  Statement, LoadingSpinner, LanguageSwitch, GifterOrGifteeModule,
  InputTokenForm, MicrositeFooter, CardFunctions, WalletButton, WalletAddedIndicator
}